import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { getPackageLinesApi } from "api/package";
import { PackageLine, PackagesNotReleased } from "interface/package";
import { GrideDateFormatter } from "ui/Date";
import { formatAmount, formatWeight } from "utils/formatters";
import { ICON_NAMES } from "utils/constant";

import "../salesNote/styles.css";

interface UnReleasedPackagesMobileListProps {
	items: PackagesNotReleased[];
	loading: boolean;
}

const getPackageKey = (item: PackagesNotReleased) =>
	`${item.registrationId}-${item.packageId}`;

const UnReleasedPackagesMobileList: FC<UnReleasedPackagesMobileListProps> = ({
	items,
	loading,
}) => {
	const { t } = useTranslation();
	const [expandedPackages, setExpandedPackages] = useState<string[]>([]);
	const [lineMap, setLineMap] = useState<Record<string, PackageLine[]>>({});
	const [lineLoadingMap, setLineLoadingMap] = useState<Record<string, boolean>>(
		{},
	);

	const empty = useMemo(
		() => !loading && items.length === 0,
		[items.length, loading],
	);

	const toggleLines = (item: PackagesNotReleased) => {
		const packageKey = getPackageKey(item);

		if (expandedPackages.includes(packageKey)) {
			setExpandedPackages((previous) =>
				previous.filter((key) => key !== packageKey),
			);
			return;
		}

		setExpandedPackages((previous) => [...previous, packageKey]);
		if (lineMap[packageKey] || lineLoadingMap[packageKey]) return;

		setLineLoadingMap((previous) => ({
			...previous,
			[packageKey]: true,
		}));

		getPackageLinesApi({
			registrationId: item.registrationId,
			packageId: item.packageId,
		})
			.then((response) => {
				setLineMap((previous) => ({
					...previous,
					[packageKey]: response?.data || [],
				}));
			})
			.finally(() => {
				setLineLoadingMap((previous) => ({
					...previous,
					[packageKey]: false,
				}));
			});
	};

	return (
		<div className="sales-note-list-mobile">
			{empty && (
				<div className="sales-note-list-mobile-empty">{t("NoRows")}</div>
			)}

			{items.map((item) => {
				const packageKey = getPackageKey(item);
				const expanded = expandedPackages.includes(packageKey);
				const lines = lineMap[packageKey] || [];
				const linesLoading = lineLoadingMap[packageKey] === true;

				return (
					<div key={packageKey} className="sales-note-list-card">
						<div className="sales-note-list-card-header">
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									flexDirection: "row",
								}}
								className="span_display_block sales-note-list-card-note "
							>
								<div>{item.registrationId}</div>
								<div>{item.packageId || "-"}</div>
							</div>
						</div>

						<div
							className="sales-note-list-card-body"
							style={{ width: "100%" }}
						>
							<div>
								<span>{t("VesselRegistrationId")}</span>
								<strong>{item.vesselRegistrationId?.trim() || "-"}</strong>
							</div>
							<div>
								<span>{t("Buyer")}</span>
								<strong>{item.buyerName || "-"}</strong>
							</div>

							<div>
								<span>{t("Type")}</span>
								<strong>{item.salesType || "-"}</strong>
							</div>

							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									flexDirection: "row",
								}}
								className="span_display_block"
							>
								<div>
									<span>{t("SalesDate")}</span>
									<strong>
										<GrideDateFormatter
											date={item.salesDate}
											formatType="dd.MM.yyyy"
											noTd={true}
										/>
									</strong>
								</div>
								<div>
									<span>{t("RegistrationDateShort")}</span>
									<strong>
										<GrideDateFormatter
											date={item.registrationDate}
											formatType="dd.MM.yyyy"
											noTd={true}
										/>
									</strong>
								</div>
							</div>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									flexDirection: "row",
								}}
								className="span_display_block"
							>
								<div>
									<span>{t("Weight")}</span>
									<strong>{formatWeight(item.liveWeightKg)}</strong>
								</div>
								<div>
									<span>{t("BuyerGrossAmount")}</span>
									<strong>{formatAmount(item.grossAmount)}</strong>
								</div>
							</div>
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									flexDirection: "row",
								}}
								className="span_display_block"
							>
								<div>
									<span>{t("Vat")}</span>
									<strong>{formatAmount(item.vatAmount)}</strong>
								</div>
								<div>
									<span>{t("BuyerNetAmount")}</span>
									<strong>{formatAmount(item.netAmount)}</strong>
								</div>
							</div>
						</div>

						<div className="sales-note-list-card-actions">
							<button
								type="button"
								className="sales-note-list-icon-btn"
								onClick={() => toggleLines(item)}
								title={t("Lines")}
							>
								<img
									src={`/icon/${expanded ? ICON_NAMES.ArrowUp : ICON_NAMES.ArrowDown}.svg`}
									alt={t("Lines")}
								/>
							</button>
						</div>

						{expanded && (
							<div className="sales-note-list-lines">
								{linesLoading && (
									<div className="sales-note-list-lines-loading">
										{t("Loading")}
									</div>
								)}
								{!linesLoading && lines.length === 0 && (
									<div className="sales-note-list-lines-empty">
										{t("NoRows")}
									</div>
								)}
								{!linesLoading &&
									lines.map((line, index) => (
										<div
											key={`${line.id}-${line.lineNumber}-${index}`}
											className="sales-note-list-line-row"
										>
											<div className="sales-note-list-line-number">
												#{line.lineNumber}
											</div>
											<div className="sales-note-list-line-product">
												{line.description || "-"}
											</div>
											<div className="sales-note-list-line-weight">
												{formatWeight(line.liveWeightKg)}
											</div>
										</div>
									))}
							</div>
						)}
					</div>
				);
			})}

			{loading && (
				<div className="sales-note-list-mobile-loading">{t("Loading")}</div>
			)}
		</div>
	);
};

export default UnReleasedPackagesMobileList;
